import { NodeDocContent } from "./nodeDocsData";

export const conditionalNodesDocs: Record<string, NodeDocContent> = {
  "IF-ELSE": {
    title: "If-Else Branch",
    subtitle: "Logical Routing",
    color: "violet",
    badge: "Conditional",
    description:
      "Routes data through different paths based on whether specified conditions are met. This is the primary way to add decision-making logic to your workflows.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Logical operators supported: <strong>EQUALS</strong>, <strong>GREATER THAN</strong>, <strong>CONTAINS</strong>, <strong>EXISTS</strong>, and more. Combine multiple conditions using <strong>AND</strong> or <strong>OR</strong> logic.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The If-Else node evaluates one or more conditions against the incoming data and routes it to either the <strong>True</strong> path or the <strong>False</strong> path based on the result.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Receive data</p>
                <p class="wf-step-desc">Accepts any data structure from previous nodes for evaluation.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Evaluate conditions</p>
                <p class="wf-step-desc">Checks your configured rules (variable + operator + value) against the incoming data.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Route to branch</p>
                <p class="wf-step-desc">Triggers the <strong>True</strong> path if conditions match, otherwise the <strong>False</strong> path.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Conditions</code></td>
                <td>A list of rules, each consisting of a variable, an operator (EQUALS, CONTAINS, etc.), and a comparison value.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Logical Operator</code></td>
                <td>How multiple conditions are joined. <strong>AND</strong> requires all to be true, <strong>OR</strong> requires at least one.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Available operators</h2>
          <table>
            <thead>
              <tr>
                <th>Operator</th>
                <th>Description</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>EQUALS</code></td><td>Exact match comparison</td><td><code>status</code> equals <code>"approved"</code></td></tr>
              <tr><td><code>GREATER THAN</code></td><td>Numeric comparison</td><td><code>confidence</code> greater than <code>0.85</code></td></tr>
              <tr><td><code>CONTAINS</code></td><td>Substring match</td><td><code>subject</code> contains <code>"invoice"</code></td></tr>
              <tr><td><code>EXISTS</code></td><td>Checks if a field is present and non-null</td><td><code>invoice_number</code> exists</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>Quality control</strong> — route documents for manual review if OCR confidence is below a threshold.</li>
            <li><strong>Data validation</strong> — check if required fields (like Invoice Number) exist before database insertion.</li>
            <li><strong>Priority routing</strong> — process high-priority files using a different set of AI models or faster pipeline.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use AND logic when all conditions must be true simultaneously.</li>
            <li>For more than 3 branches on the same variable, consider using a Switch node instead.</li>
            <li>You can chain multiple If-Else nodes for complex multi-level decision trees.</li>
          </ul>
        </div>
      </div>
    `,
  },

  SWITCH: {
    title: "Switch / Router",
    subtitle: "Multi-path Routing",
    color: "indigo",
    badge: "Conditional",
    description:
      "Routes data through multiple alternative paths based on specific values. Think of it as a multi-way branch where each path matches a specific 'Case'.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The Switch node is more efficient than nested If-Else nodes when you have <strong>3 or more potential paths</strong> for the same data variable.
        </div>

        <section>
          <h2>How it works</h2>
          <p>Select a variable to evaluate, define multiple cases (rules), and the node routes data to the matching output path. If no case matches, data flows to the fallback "Else" path.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Select input variable</p>
                <p class="wf-step-desc">Choose a variable from previous nodes to evaluate, e.g. <code>document_type</code>.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Define cases</p>
                <p class="wf-step-desc">Create multiple rules. Each rule maps to a physical output handle on the node.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Routing</p>
                <p class="wf-step-desc">Rules are checked in order. The first match triggers its output path.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">4</div>
              <div>
                <p class="wf-step-title">Fallback</p>
                <p class="wf-step-desc">If no rules match, the "Else" path is triggered as the catch-all.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Output handles</h2>
          <p>Unlike most nodes, the Switch node has <strong>variable output ports</strong>. Each port corresponds to a case you define:</p>
          <table>
            <thead>
              <tr>
                <th>Port</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>0</code></td><td>First case match</td></tr>
              <tr><td><code>1</code></td><td>Second case match</td></tr>
              <tr><td><code>2</code></td><td>Third case match (and so on)</td></tr>
              <tr><td><code>else</code></td><td>Fallback — triggers when no case matches</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Switch vs If-Else</h2>
          <table>
            <thead>
              <tr>
                <th>Scenario</th>
                <th>Recommended</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Simple true/false check</td><td>If-Else</td></tr>
              <tr><td>2 branches on different variables</td><td>If-Else</td></tr>
              <tr><td>3+ branches on the same variable</td><td>Switch</td></tr>
              <tr><td>Routing by document type or category</td><td>Switch</td></tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Cases are evaluated top-to-bottom — put the most common matches first for efficiency.</li>
            <li>Always connect the "Else" handle to a fallback path for unexpected values.</li>
            <li>Use this node to route documents by type (invoice, receipt, contract, etc.).</li>
          </ul>
        </div>
      </div>
    `,
  },

  "FOR-EACH": {
    title: "For Each Loop",
    subtitle: "Iterative Processing",
    color: "emerald",
    badge: "Conditional",
    description:
      "Iterates over a list or array of items, executing a specified sequence of nodes for each individual item. Perfect for processing multiple files or extracted line items.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          The For Each node has two distinct outputs: <strong>Loop</strong> (triggers for every item) and <strong>Done</strong> (triggers once after the entire list is processed).
        </div>

        <section>
          <h2>Loop lifecycle</h2>
          <p>The node receives an array, iterates through each item sequentially, and waits for the downstream flow to complete before moving to the next item.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Receive array</p>
                <p class="wf-step-desc">Accepts an array output from a previous node (e.g. a list of extracted line items).</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Trigger Loop path</p>
                <p class="wf-step-desc">Fires the <strong>Loop</strong> output for the first item in the list.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Wait and repeat</p>
                <p class="wf-step-desc">Waits for the downstream flow to complete, then fires again for the next item.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Trigger Done path</p>
                <p class="wf-step-desc">Once all items are processed, triggers the <strong>Done</strong> output to continue the main workflow.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Output handles</h2>
          <table>
            <thead>
              <tr>
                <th>Handle</th>
                <th>Fires</th>
                <th>Use for</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>Loop</code></td><td>Once per item</td><td>Processing individual items (OCR, API calls, transforms)</td></tr>
              <tr><td><code>Done</code></td><td>Once after all items</td><td>Aggregation, final export, or notification after batch completes</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Multi-file processing</p>
              <p class="wf-use-case-flow">OneDrive Trigger &rarr; For Each &rarr; OCR &rarr; Database</p>
              <p>Iterate over a folder of PDFs from OneDrive and run OCR on each one individually.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Line item enrichment</p>
              <p class="wf-use-case-flow">Smart Parse &rarr; For Each &rarr; HTTP API &rarr; Transform</p>
              <p>Take a list of extracted product codes and call an external API for each to fetch pricing data.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Items are processed sequentially, not in parallel — each item waits for the previous to complete.</li>
            <li>Connect the "Done" handle to export or notification nodes for post-batch actions.</li>
            <li>Combine with IF-ELSE inside the loop to conditionally skip certain items.</li>
          </ul>
        </div>
      </div>
    `,
  },
};
